import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BookOpen, Upload, Loader, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';

const GENRE_OPTIONS = [
  'Fantasy', 'Science Fiction', 'Romance', 'Mystery',
  'Thriller', 'Horror', 'Historical Fiction', 'Literary Fiction',
  'Young Adult', 'Children\'s', 'Biography', 'Non-fiction'
];

export default function EditIdea() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [customTags, setCustomTags] = useState('');
  const [coverImage, setCoverImage] = useState<File | null>(null);
  const [currentCoverUrl, setCurrentCoverUrl] = useState<string | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(null);
  const [deletingCover, setDeletingCover] = useState(false);

  useEffect(() => {
    const checkAuthAndFetchIdea = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/auth', { 
          state: { returnTo: `/idea/${id}/edit` },
          replace: true
        });
        return;
      }

      try {
        const { data: idea, error: ideaError } = await supabase
          .from('book_ideas')
          .select('*')
          .eq('id', id)
          .single();

        if (ideaError) throw ideaError;

        if (idea.author_id !== user.id) {
          navigate(`/idea/${id}`, { replace: true });
          return;
        }

        setTitle(idea.title);
        setDescription(idea.description);
        setSelectedGenres(idea.genre);
        setCurrentCoverUrl(idea.cover_image);
        setCoverPreview(idea.cover_image);
        setCustomTags(idea.custom_tags?.join(', ') || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load book idea');
      } finally {
        setLoading(false);
      }
    };

    checkAuthAndFetchIdea();
  }, [id, user, authLoading, navigate]);

  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev =>
      prev.includes(genre)
        ? prev.filter(g => g !== genre)
        : prev.length < 3
        ? [...prev, genre]
        : prev
    );
  };

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteCover = async () => {
    if (!user || !currentCoverUrl) return;
    
    try {
      setDeletingCover(true);
      setError(null);

      // Extract the file name from the URL
      const fileName = currentCoverUrl.split('/').pop();
      if (!fileName) throw new Error('Invalid cover image URL');

      // Delete the file from storage
      const { error: storageError } = await supabase.storage
        .from('covers')
        .remove([fileName]);

      if (storageError) throw storageError;

      // Update the book idea
      const { error: updateError } = await supabase
        .from('book_ideas')
        .update({
          cover_image: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Clear the cover image states
      setCurrentCoverUrl(null);
      setCoverPreview(null);
      setCoverImage(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete cover image');
    } finally {
      setDeletingCover(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    try {
      setSaving(true);
      setError(null);

      let coverImageUrl = currentCoverUrl;
      if (coverImage) {
        const fileExt = coverImage.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('covers')
          .upload(fileName, coverImage);

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('covers')
          .getPublicUrl(fileName);
          
        coverImageUrl = publicUrl;
      }

      // Process custom tags
      const processedTags = customTags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Update the book idea
      const { error: updateError } = await supabase
        .from('book_ideas')
        .update({
          title,
          description,
          genre: selectedGenres,
          cover_image: coverImageUrl,
          custom_tags: processedTags,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (updateError) throw updateError;

      navigate(`/idea/${id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update book idea');
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loader className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
        <div className="flex items-center gap-2 mb-6">
          <BookOpen className="w-6 h-6 text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-900">Edit Book Idea</h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              rows={6}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={customTags}
              onChange={(e) => setCustomTags(e.target.value)}
              placeholder="e.g., magic system, dragons, epic battles, enemies to lovers, chosen one"
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <p className="mt-1 text-sm text-gray-500">
              Add any relevant tags including story elements, tropes, themes, etc.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Genres (select up to 3)
            </label>
            <div className="flex flex-wrap gap-2">
              {GENRE_OPTIONS.map((genre) => (
                <button
                  key={genre}
                  type="button"
                  onClick={() => handleGenreToggle(genre)}
                  disabled={selectedGenres.length >= 3 && !selectedGenres.includes(genre)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedGenres.includes(genre)
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  } ${
                    selectedGenres.length >= 3 && !selectedGenres.includes(genre)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  {genre}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cover Image
            </label>
            <div className="flex items-start gap-4">
              {coverPreview && (
                <div className="relative">
                  <img
                    src={coverPreview}
                    alt="Cover preview"
                    className="w-32 h-32 object-cover rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={handleDeleteCover}
                    disabled={deletingCover}
                    className="absolute -top-2 -right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
              <label className="flex-1">
                <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 text-center">
                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  {coverPreview ? 'Choose new cover' : 'Choose cover'}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={handleCoverChange}
                />
              </label>
            </div>
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate(`/idea/${id}`)}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !title || !description || selectedGenres.length === 0}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}